import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Breadcrumbs,
    Link,
    useTheme,
} from '@mui/material';
import {
    NavigateNext as NavigateNextIcon,
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    PersonAdd as PersonAddIcon,
    AttachMoney as AttachMoneyIcon,
    Event as EventIcon,
    Key as KeyIcon,
} from '@mui/icons-material';
import Seo from '../../components/common/Seo';

function GettingStarted() {
    const theme = useTheme();

    const steps = [
        {
            title: '自分の希望を明確にする',
            description: 'ルームシェアを始める前に、予算、希望エリア、同居人の条件などを明確にしましょう。',
            icon: <CheckCircleIcon color="primary" />,
            content: (
                <>
                    <Typography variant="body1" paragraph>
                        ルームシェアを成功させるための第一歩は、自分自身の希望や条件を明確にすることです。以下のポイントを考慮してみましょう：
                    </Typography>
                    <List sx={{ pl: 2 }}>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <AttachMoneyIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="予算"
                                secondary="家賃、光熱費、インターネット代などの月々の支出をどれくらい考えていますか？"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <HomeIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="希望エリア"
                                secondary="通勤・通学のしやすさや生活環境を考慮して、希望するエリアを決めましょう。"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <PersonAddIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="同居人の条件"
                                secondary="性別、年齢、職業、生活習慣などの希望条件を整理しておきましょう。"
                            />
                        </ListItem>
                    </List>
                    <Box sx={{ mt: 2, bgcolor: '#E3F2FD', p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="primary">
                            ポイント
                        </Typography>
                        <Typography variant="body2">
                            妥協できる条件と絶対に譲れない条件を分けて考えておくと、後々のミスマッチを防ぐことができます。
                        </Typography>
                    </Box>
                </>
            )
        },
        {
            title: 'ルームメイトを探す',
            description: '当サイトを活用して、条件に合うルームメイトを探しましょう。',
            icon: <PersonAddIcon color="primary" />,
            content: (
                <>
                    <Typography variant="body1" paragraph>
                        理想のルームメイトを見つけるためには、以下の方法があります：
                    </Typography>
                    <List sx={{ pl: 2 }}>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="当サイトのルームメイト検索を活用する"
                                secondary="条件に合ったルームメイトを検索し、プロフィールを確認しましょう。"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="自分の希望条件を掲載する"
                                secondary="自分のプロフィールと希望条件を掲載すれば、条件に合う人から連絡が来る可能性があります。"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="メッセージでコミュニケーションを取る"
                                secondary="気になる相手には積極的にメッセージを送り、相性を確認しましょう。"
                            />
                        </ListItem>
                    </List>
                    <Box sx={{ mt: 2, bgcolor: '#FFF3E0', p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#E65100' }}>
                            注意点
                        </Typography>
                        <Typography variant="body2">
                            初対面の人と暮らすことになるため、事前の十分なコミュニケーションが大切です。可能であれば直接会って話す機会を設けることをおすすめします。
                        </Typography>
                    </Box>
                </>
            )
        },
        {
            title: '物件を探す・契約する',
            description: '条件に合う物件を探し、契約の際には内容をしっかり確認しましょう。',
            icon: <KeyIcon color="primary" />,
            content: (
                <>
                    <Typography variant="body1" paragraph>
                        ルームメイトが決まったら、一緒に住む物件を探しましょう。または既に物件を持っている場合は、契約内容の確認が必要です。
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        新しく物件を探す場合
                    </Typography>
                    <List sx={{ pl: 2 }}>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="ルームシェア可能な物件を探す"
                                secondary="不動産サイトやルームシェア専門サイトで「ルームシェア可」の物件を探しましょう。"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="内見は一緒に行く"
                                secondary="可能であれば、一緒に住む予定の人と内見に行きましょう。生活イメージを共有できます。"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="契約者と入居者の関係を明確にする"
                                secondary="契約者が誰になるのか、家賃の支払い方法などを事前に決めておきましょう。"
                            />
                        </ListItem>
                    </List>

                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                        既に物件を持っている場合
                    </Typography>
                    <List sx={{ pl: 2 }}>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="大家さんの許可を確認"
                                secondary="賃貸契約でルームシェアが認められているか確認しましょう。無断でルームシェアすると契約違反になる場合があります。"
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                                primary="転貸借契約の検討"
                                secondary="正式に契約者と入居者の間で契約を結ぶことで、トラブル防止につながります。"
                            />
                        </ListItem>
                    </List>

                    <Box sx={{ mt: 2, bgcolor: '#E8F5E9', p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#2E7D32' }}>
                            アドバイス
                        </Typography>
                        <Typography variant="body2">
                            契約前に、退去時の条件や敷金の扱い、修繕負担などについても確認・合意しておくことが重要です。
                        </Typography>
                    </Box>
                </>
            )
        },
        {
            title: 'ルールを決める',
            description: '共同生活を円滑に進めるために、生活ルールや費用分担などを事前に決めておきましょう。',
            icon: <EventIcon color="primary" />,
            content: (
                <>
                    <Typography variant="body1" paragraph>
                        快適なルームシェア生活のために、以下のようなルールを事前に話し合っておくことをおすすめします。
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            共有スペースの使用ルール
                        </Typography>
                        <Typography variant="body2" paragraph>
                            リビングやキッチン、バスルームなどの共有スペースの使用時間や使用後の片付けについてのルールを決めておきましょう。
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
                            光熱費などの支払い
                        </Typography>
                        <Typography variant="body2" paragraph>
                            水道代、電気代、ガス代、インターネット代などの支払い方法や分担割合を決めておきましょう。
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
                            生活時間
                        </Typography>
                        <Typography variant="body2" paragraph>
                            起床・就寝時間や入浴時間など、お互いの生活リズムを尊重できるように話し合っておきましょう。
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
                            掃除当番
                        </Typography>
                        <Typography variant="body2" paragraph>
                            共有スペースの掃除を誰がいつ行うか、当番制にするか共同で行うかなどを決めておきましょう。
                        </Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>
                            ゲスト対応
                        </Typography>
                        <Typography variant="body2" paragraph>
                            友人や家族を招く際のルール（事前連絡の有無、宿泊の可否など）について話し合っておきましょう。
                        </Typography>
                    </Box>

                    <Box sx={{ bgcolor: '#F3E5F5', p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: '#7B1FA2' }}>
                            ポイント
                        </Typography>
                        <Typography variant="body2">
                            ルールは書面にまとめておくと良いでしょう。また、定期的にルームメイトとコミュニケーションを取り、必要に応じてルールを見直すことも大切です。
                        </Typography>
                    </Box>
                </>
            )
        },
    ];

    return (
        <>
            <Seo
                title="ルームシェアの始め方ガイド"
                description="ルームシェアを始める方のための基本ガイド。希望条件の決め方からルームメイト探し、物件契約、生活ルールの決め方まで、初めてのルームシェアを成功させるためのポイントを解説します。"
                path="/guide/getting-started"
                keywords="ルームシェアの始め方,ルームメイト探し,物件探し,生活ルール,初めてのルームシェア"
            />
            <Container maxWidth="md">
                <Paper sx={{ p: { xs: 2, md: 4 }, mt: 4, mb: 4, borderRadius: 2 }}>
                    {/* パンくずリスト */}
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        sx={{ mb: 3 }}
                    >
                        <Link component={RouterLink} to="/" color="inherit">
                            トップ
                        </Link>
                        <Link component={RouterLink} to="/guide" color="inherit">
                            ガイド
                        </Link>
                        <Typography color="text.primary">ルームシェアの始め方</Typography>
                    </Breadcrumbs>

                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', md: '2.5rem' }
                        }}
                    >
                        ルームシェアの始め方
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        <Chip label="初心者向け" size="small" color="primary" variant="outlined" />
                        <Chip label="基本ガイド" size="small" color="info" variant="outlined" />
                    </Box>

                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        初めてのルームシェアは不安もあるかもしれませんが、適切な準備と心構えがあれば、
                        快適で充実した共同生活を送ることができます。このガイドでは、ルームシェアを始める際に
                        知っておきたい基本的な情報と準備のポイントをご紹介します。
                    </Typography>

                    <Divider sx={{ mb: 4 }} />

                    {/* ステップごとのコンテンツ */}
                    {steps.map((step, index) => (
                        <Box key={index} sx={{ mb: 5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {index + 1}
                                </Box>
                                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                                    {step.title}
                                </Typography>
                            </Box>

                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, pl: 6 }}>
                                {step.description}
                            </Typography>

                            <Box sx={{ pl: 6 }}>
                                {step.content}
                            </Box>

                            {index < steps.length - 1 && (
                                <Divider sx={{ mt: 4 }} />
                            )}
                        </Box>
                    ))}

                    <Box sx={{ mt: 6, p: 3, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            次のステップ
                        </Typography>
                        <Typography variant="body2" paragraph>
                            ルームシェアの基本がわかったら、次は具体的な募集方法について学びましょう。
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/guide/how-to-post"
                            color="primary"
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            sx={{ mt: 1 }}
                        >
                            募集の書き方のコツを見る
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}

export default GettingStarted; 